import { HexColor } from './color.type';

export interface PersonBbox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly confidence: number;
}

/**
 * Один адрес — одна серверная версия файла; имя поля называет её длинную сторону в пикселях.
 * Нарезает версии image-transform-service: THUMB 320, SMALL 640, MEDIUM 1280, LARGE 1920.
 *
 * Поля необязательные, потому что набор версий зависит от даты загрузки фото: свежие несут
 * 320/1280/1920 (SMALL больше не режется), давние — 320/640/1280 и без 1920. Отсутствующую
 * версию оставляем пустой. Подставить под её именем соседний размер нельзя: именно такая
 * подмена заставляла лестницу рендера считать 1280-й файл шестисотсороковым.
 */
export interface ImageAlbumSizes {
  /** LARGE — 1920 по длинной стороне. */
  readonly big_1920?: string;
  /** MEDIUM — 1280 по длинной стороне. */
  readonly medium_1280?: string;
  /** SMALL — 640 по длинной стороне. У фото, загруженных после перехода на LARGE, версии нет. */
  readonly small?: string;
  /** ORIGINAL — исходник как есть, без ограничения стороны. Печать и скачивание, не холст. */
  readonly image: string;
  /** THUMB — 320 по длинной стороне. */
  readonly thumb?: string;
}

/** An `image` the browser can draw: a HEIC/HEIF original never decodes in a page outside Safari, its JPEG versions do. */
export function browserDrawableOriginal(url: string | undefined): string | undefined {
  return url && !/\.hei[cf](?:[?#]|$)/i.test(url) ? url : undefined;
}

/** Culling stars on a bin photo; 0 = unrated. */
export type ImageRating = 0 | 1 | 2 | 3 | 4 | 5;

export interface ImageAlbum extends ImageAlbumSizes {
  readonly id: string;
  readonly exif_data?: {
    readonly DateTimeOriginal: string;
  };
  readonly filename: string;
  readonly height: number;
  readonly width: number;
  readonly image_dominant_color: HexColor;
  readonly thumb_hash?: string;
  readonly faces?: readonly unknown[];
  readonly personId?: string | null;
  readonly personBboxes?: readonly PersonBbox[];
  readonly tags?: readonly string[];
  /** Bin-copy concern: frame copies keep the snapshot taken at assignment and never read it. */
  readonly rating?: ImageRating;
}
